// The invisible captcha the public forms that render Shared/RecaptchaHandler.razor submit through: Google
// reCAPTCHA v2 invisible, or Cloudflare Turnstile for the countries where Google's api.js cannot be reached.
// Loaded once from App.razor, out of the component's own <script> block (tokero-style §19.3.8).
//
// The provider's script is not fetched when the component renders but on the visitor's first interaction with
// the form the component sits in - a pointer press or a key - or, at the latest, when the form submits and asks
// for a token. The script and the bundle it pulls in are heavy, most visitors who see a form never submit it,
// and fetching them on render made every visitor pay for them before the page was usable. A focus is
// deliberately not a trigger: the login form focuses its own email field on a desktop first render, which would
// fetch the script on render again for every desktop visitor.
//
// The page-facing contract is unchanged: a page calls loadCaptcha once from the component's first render and
// awaits window.executeInvisibleRecaptcha(containerID) for a token on submit. When the provider script is still
// in flight at that moment, the promise waits for the widget and executes it the moment it renders, so a submit
// right after the first keystroke still gets a token instead of the empty-token error.
(function () {
  if (window.loadCaptcha) {
    return;
  }

  // One entry per container id. Every page passes the same id (RecaptchaHandler.RecaptchaContainerId); the maps
  // keep the per-container shape the executeInvisibleRecaptcha contract has always had.
  const widgetIDsByContainerID = {};
  const providersByContainerID = {};
  const siteKeysByContainerID = {};
  const resolversByContainerID = {};
  // True from the first interaction (or submit) that asked for the widget; later asks do nothing.
  const loadRequestedByContainerID = {};
  // A submit that arrived before the widget rendered. The render executes it.
  const executePendingByContainerID = {};
  // "loading" from the moment the provider's script tag is appended, "ready" from its onload callback. The script
  // outlives an enhanced navigation, so the next page that needs it finds it "ready" and renders its widget at once;
  // one that arrives while it is still "loading" is rendered by the same onload callback.
  const scriptStateByProvider = {};

  // The Turnstile fallback. A container served reCAPTCHA that was also handed a Turnstile site key switches to
  // Turnstile when Google cannot be reached from this browser: its script fails to load, has not rendered in time for
  // a waiting submit, or the widget reports an error. A token the fallback produced carries the prefix loadCaptcha is
  // handed (RecaptchaService.TurnstileFallbackTokenPrefix), which is how the server knows to verify it at Cloudflare.
  // Once Google has failed on this document, later containers go straight to the fallback rather than each waiting
  // for it to fail again.
  const fallbackSiteKeysByContainerID = {};
  const fallbackTokenPrefixesByContainerID = {};
  const isFallbackByContainerID = {};
  let isRecaptchaUnreachable = false;
  // The wait a submit is currently timed by, per container. A timer that finds another wait in its place stands down:
  // the switch to the fallback starts a new wait, and the old timer must not settle the submit before it ends.
  const widgetWaitMarkersByContainerID = {};

  const turnstileProviderName = "turnstile";
  const recaptchaProviderName = "recaptcha";

  // A submit that waits for a provider script which never arrives - blocked network, blocked domain - gives up
  // after this long and settles empty, which the server rejects with the captcha error, instead of hanging the form.
  // A widget that did render is not timed: a visual challenge takes as long as the visitor needs.
  const widgetWaitTimeoutMs = 15000;

  // How long a submit waits for Google's widget before a container that has the fallback switches to it. Shorter than
  // the timeout above, which would otherwise run once for Google and once more for Turnstile.
  const recaptchaFallbackWaitMs = 5000;

  // Hands the pending executeInvisibleRecaptcha promise its result exactly once. An empty token is a real outcome
  // here, not a missing one: the server rejects it and the visitor gets the captcha error message, where a promise
  // left unsettled would leave the form appearing to do nothing at all.
  function settleCaptchaToken(containerID, token) {
    const resolver = resolversByContainerID[containerID];
    resolversByContainerID[containerID] = null;
    if (resolver) {
      resolver(token);
    }
  }

  function executeCaptchaWidget(containerID) {
    const widgetID = widgetIDsByContainerID[containerID];
    if (providersByContainerID[containerID] === turnstileProviderName) {
      turnstile.execute(widgetID);
      return;
    }

    grecaptcha.execute(widgetID);
  }

  // Runs the submit that was waiting for this widget, if there is one.
  function onCaptchaWidgetRendered(containerID) {
    if (!executePendingByContainerID[containerID]) {
      return;
    }

    executePendingByContainerID[containerID] = false;
    executeCaptchaWidget(containerID);
  }

  function renderRecaptchaWidget(containerID) {
    const container = document.getElementById(containerID);
    if (!container) {
      console.error("Captcha container not found: " + containerID);
      return;
    }

    const widgetID = grecaptcha.render(container, {
      'sitekey': siteKeysByContainerID[containerID],
      'size': 'invisible',
      'callback': function (token) {
        settleCaptchaToken(containerID, token);
        grecaptcha.reset(widgetID);
      },
      'error-callback': function () {
        console.error("Captcha error in widget: " + containerID);
        // Google reports a lost connection here. A submit waiting on this widget carries on through the fallback,
        // whose render executes it.
        const isSubmitWaiting = resolversByContainerID[containerID] != null;
        executePendingByContainerID[containerID] = isSubmitWaiting;
        const isFallbackStarted = switchToTurnstileFallback(containerID);
        if (isFallbackStarted) {
          return;
        }

        executePendingByContainerID[containerID] = false;
        settleCaptchaToken(containerID, "");
      }
    });
    widgetIDsByContainerID[containerID] = widgetID;
    onCaptchaWidgetRendered(containerID);
  }

  function renderTurnstileWidget(containerID) {
    const container = document.getElementById(containerID);
    if (!container) {
      console.error("Captcha container not found: " + containerID);
      return;
    }

    const widgetID = turnstile.render(container, {
      'sitekey': siteKeysByContainerID[containerID],
      // execution 'execute' holds the challenge until turnstile.execute is called, and appearance
      // 'interaction-only' keeps the widget out of sight unless Cloudflare decides this visitor has to touch
      // something - which is the same bargain the invisible reCAPTCHA offers.
      'execution': 'execute',
      'appearance': 'interaction-only',
      'callback': function (token) {
        let submittedToken = token;
        if (isFallbackByContainerID[containerID]) {
          submittedToken = fallbackTokenPrefixesByContainerID[containerID] + token;
        }
        settleCaptchaToken(containerID, submittedToken);
        turnstile.reset(widgetID);
      },
      // Returning true tells Turnstile the error is handled and suppresses its own error UI, which is right here:
      // the empty token above already routes the visitor to the form's captcha message.
      'error-callback': function () {
        console.error("Captcha error in widget: " + containerID);
        settleCaptchaToken(containerID, "");
        return true;
      }
    });
    widgetIDsByContainerID[containerID] = widgetID;
    onCaptchaWidgetRendered(containerID);
  }

  function renderCaptchaWidget(containerID) {
    if (providersByContainerID[containerID] === turnstileProviderName) {
      renderTurnstileWidget(containerID);
      return;
    }

    renderRecaptchaWidget(containerID);
  }

  // Every container that asked for this provider's widget and has none yet: the one that triggered the fetch, or
  // the one an enhanced navigation created while the script was still on its way.
  function renderPendingCaptchaWidgets(provider) {
    Object.keys(loadRequestedByContainerID).forEach(function (containerID) {
      if (!loadRequestedByContainerID[containerID]) {
        return;
      }
      if (providersByContainerID[containerID] !== provider) {
        return;
      }
      if (widgetIDsByContainerID[containerID] != null) {
        return;
      }

      renderCaptchaWidget(containerID);
    });
  }

  // The provider script could not be fetched. A submit waiting on Google's moves to the Turnstile fallback when its
  // container has one; every other submit waiting on it gets the captcha error now, and the next submit fetches the
  // script again rather than waiting on a tag that already failed.
  function onCaptchaScriptFailed(provider) {
    scriptStateByProvider[provider] = undefined;

    Object.keys(loadRequestedByContainerID).forEach(function (containerID) {
      if (providersByContainerID[containerID] !== provider) {
        return;
      }

      // A container that asked for Google's widget moves to the fallback, keeping any submit that waits on it.
      if (loadRequestedByContainerID[containerID]) {
        const isFallbackStarted = switchToTurnstileFallback(containerID);
        if (isFallbackStarted) {
          return;
        }
      }

      loadRequestedByContainerID[containerID] = false;
      executePendingByContainerID[containerID] = false;
      settleCaptchaToken(containerID, "");
    });
  }

  // The ?onload= callbacks the provider scripts name. Globals because the providers call them by name.
  window.onRecaptchaLoaded = function () {
    scriptStateByProvider[recaptchaProviderName] = "ready";
    renderPendingCaptchaWidgets(recaptchaProviderName);
  };

  window.tkOnTurnstileLoaded = function () {
    scriptStateByProvider[turnstileProviderName] = "ready";
    renderPendingCaptchaWidgets(turnstileProviderName);
  };

  function appendCaptchaProviderScript(provider) {
    const providerScript = document.createElement('script');
    if (provider === turnstileProviderName) {
      providerScript.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=tkOnTurnstileLoaded&render=explicit';
    } else {
      providerScript.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoaded&render=explicit';
    }
    providerScript.async = true;
    providerScript.defer = true;
    providerScript.onerror = function () {
      console.error("Captcha provider script failed to load: " + provider);
      onCaptchaScriptFailed(provider);
    };
    scriptStateByProvider[provider] = "loading";
    document.body.appendChild(providerScript);
  }

  // Fetches the provider script, or renders the widget straight away when the script is already here. Safe to
  // call any number of times: the first call per container does the work, every later one returns at once.
  function ensureCaptchaWidgetLoaded(containerID) {
    if (loadRequestedByContainerID[containerID]) {
      return;
    }
    if (isRecaptchaUnreachable && switchToTurnstileFallback(containerID)) {
      return;
    }
    loadRequestedByContainerID[containerID] = true;

    const provider = providersByContainerID[containerID];
    const scriptState = scriptStateByProvider[provider];
    if (scriptState === "ready") {
      renderCaptchaWidget(containerID);
      return;
    }
    if (scriptState === "loading") {
      // The onload callback renders this container's widget together with any other that is waiting.
      return;
    }

    appendCaptchaProviderScript(provider);
  }

  // Moves a container served reCAPTCHA onto its Turnstile fallback and asks for the Turnstile widget. False, and
  // nothing changed, when there is no fallback to move to: the container is on Turnstile already, it was handed no
  // Turnstile key, or it is gone. A submit already waiting keeps waiting, the Turnstile render executes it, and it is
  // timed afresh for the Turnstile widget - whichever trigger made the switch.
  function switchToTurnstileFallback(containerID) {
    if (providersByContainerID[containerID] !== recaptchaProviderName) {
      return false;
    }
    const fallbackSiteKey = fallbackSiteKeysByContainerID[containerID];
    if (!fallbackSiteKey) {
      return false;
    }
    const container = document.getElementById(containerID);
    if (!container) {
      return false;
    }

    console.error("Captcha switching to the Turnstile fallback: " + containerID);
    isRecaptchaUnreachable = true;
    providersByContainerID[containerID] = turnstileProviderName;
    siteKeysByContainerID[containerID] = fallbackSiteKey;
    isFallbackByContainerID[containerID] = true;
    widgetIDsByContainerID[containerID] = null;
    // Turnstile renders into an empty container, and draws wherever the container sits: the same overlay class a
    // container created for Turnstile gets.
    container.innerHTML = "";
    container.classList.add("tk-captcha-overlay");
    loadRequestedByContainerID[containerID] = false;
    ensureCaptchaWidgetLoaded(containerID);

    const waitingResolver = resolversByContainerID[containerID];
    if (waitingResolver) {
      armWidgetWaitTimeout(containerID, waitingResolver);
    }

    return true;
  }

  // Settles a waiting submit empty when its widget has still not rendered after the wait - unless the container can
  // still move to the fallback, in which case the switch starts the next wait. Arming a new wait retires the one
  // before it.
  function armWidgetWaitTimeout(containerID, resolve) {
    let canFallBack = false;
    if (providersByContainerID[containerID] === recaptchaProviderName && fallbackSiteKeysByContainerID[containerID]) {
      canFallBack = true;
    }

    let waitMs = widgetWaitTimeoutMs;
    if (canFallBack) {
      waitMs = recaptchaFallbackWaitMs;
    }

    const widgetWaitMarker = {};
    widgetWaitMarkersByContainerID[containerID] = widgetWaitMarker;

    window.setTimeout(function () {
      if (widgetWaitMarkersByContainerID[containerID] !== widgetWaitMarker) {
        return;
      }
      if (widgetIDsByContainerID[containerID] != null) {
        return;
      }
      if (resolversByContainerID[containerID] !== resolve) {
        return;
      }

      const isFallbackStarted = switchToTurnstileFallback(containerID);
      if (isFallbackStarted) {
        return;
      }

      console.error("Captcha widget did not load in time: " + containerID);
      executePendingByContainerID[containerID] = false;
      settleCaptchaToken(containerID, "");
    }, waitMs);
  }

  function createCaptchaContainer(containerID, provider) {
    const captchaContainerDiv = document.createElement("div");
    captchaContainerDiv.id = containerID;
    if (provider === turnstileProviderName) {
      // .tk-captcha-overlay (App.css) pins the container centred. reCAPTCHA draws its own centred overlay when a
      // challenge is needed; Turnstile draws into this container wherever it happens to sit, and it sits at the
      // very end of the body, so without the class an interactive Turnstile challenge is parked below the fold.
      captchaContainerDiv.classList.add("tk-captcha-overlay");
    }
    document.querySelector("body").appendChild(captchaContainerDiv);
  }

  // The first pointer press or key inside the form the component sits in fetches the provider script, so that it
  // is usually here by the time the visitor submits. A component with no form around it listens on the document.
  // Listening on the form rather than its fields means a press on the submit button counts too - the
  // autofilled-then-click path - and listening in the capture phase means a key a field's own handler stops
  // (MudBlazor's key interceptor can) still counts. The listeners are one-shot and the fetch is
  // idempotent, so a form rendered twice on one document arms twice without harm.
  function armCaptchaFirstInteraction(containerID, formAnchor) {
    let interactionScope = document;
    if (formAnchor && formAnchor.closest) {
      const form = formAnchor.closest("form");
      if (form) {
        interactionScope = form;
      }
    }

    const onFirstInteraction = function () {
      ensureCaptchaWidgetLoaded(containerID);
    };
    interactionScope.addEventListener("pointerdown", onFirstInteraction, { once: true, passive: true, capture: true });
    interactionScope.addEventListener("keydown", onFirstInteraction, { once: true, passive: true, capture: true });
  }

  // Named for reCAPTCHA because every calling page has awaited it by that name since before there was a second
  // provider; it dispatches on whichever one this container was told to use.
  window.executeInvisibleRecaptcha = function (containerID) {
    return new Promise(function (resolve) {
      resolversByContainerID[containerID] = resolve;

      if (providersByContainerID[containerID] === undefined) {
        // loadCaptcha never ran for this container - the component did not render, or its script is missing.
        // Settling empty turns that into the captcha error rather than a form that hangs.
        settleCaptchaToken(containerID, "");
        return;
      }

      const widgetID = widgetIDsByContainerID[containerID];
      if (widgetID === undefined || widgetID === null) {
        // The visitor submitted before the first interaction fetched the script, or before it arrived. Ask for it
        // now (a no-op when it is already on its way) and let the render run this submit.
        executePendingByContainerID[containerID] = true;
        ensureCaptchaWidgetLoaded(containerID);
        armWidgetWaitTimeout(containerID, resolve);
        return;
      }

      executeCaptchaWidget(containerID);
    });
  };

  // Called once from the component's first render. formAnchor is an element inside the form the component sits in.
  // fallbackSiteKey is the Turnstile key to switch to when Google cannot be reached, or null for no fallback, and
  // fallbackTokenPrefix the marker a fallback token carries.
  window.loadCaptcha = function (containerID, siteKey, provider, formAnchor, fallbackSiteKey, fallbackTokenPrefix) {
    // A live container that already moved to the fallback keeps it: this render would hand it Google's provider
    // again while the widget inside it is Turnstile's.
    if (isFallbackByContainerID[containerID] && document.getElementById(containerID)) {
      return;
    }

    providersByContainerID[containerID] = provider;
    siteKeysByContainerID[containerID] = siteKey;
    fallbackSiteKeysByContainerID[containerID] = fallbackSiteKey || null;
    fallbackTokenPrefixesByContainerID[containerID] = fallbackTokenPrefix || "";
    isFallbackByContainerID[containerID] = false;

    // A navigation that replaces the body takes this container - and the widget rendered into it - with it, while
    // the provider script stays loaded. So the container's presence, not a "have we run before" flag, is what
    // decides: still there means the container is still live and there is nothing to create; gone means re-create,
    // rather than later executing a widget id that no longer points at anything. A container that is there with
    // no widget yet (the visitor has not touched the form) is re-armed on the form this render put up: the one the
    // earlier listeners sit on may be gone.
    const existingContainer = document.getElementById(containerID);
    if (!existingContainer) {
      widgetIDsByContainerID[containerID] = null;
      loadRequestedByContainerID[containerID] = false;
      executePendingByContainerID[containerID] = false;
      createCaptchaContainer(containerID, provider);
    }

    if (widgetIDsByContainerID[containerID] === null && !loadRequestedByContainerID[containerID]) {
      armCaptchaFirstInteraction(containerID, formAnchor);
    }
  };
})();
